// Ink icons of the strategic view, drawn per hex from signed distances (no texture: the terrain
// shader has no free binding). Coordinates are the fragment offset from the hex centre in hex radii,
// x east and y south, so a pointy-top hex spans about ±0.87 across and ±1 up and down.
//
//        mountains        hills          woods          marsh         city
//           /\  /\         ___   __       o  o           - -          /\
//          /  \/  \       /   \_/  \       o  o          - -         |__|
//
// Every icon returns two coverage masks: `fill` (its body colour) and `stroke` (its ink outline), both
// anti-aliased at one pixel. Strokes keep a minimum world width so a close zoom still shows them.

import { float, length, max, smoothstep, vec2 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';

type F = Node<'float'>;
type V2 = Node<'vec2'>;
type Point = readonly [x: number, y: number];

export interface Mark {
  fill: F;
  stroke: F;
}

/** Stroke half-width: this many pixels, but never thinner than this many hex radii. */
const STROKE_PX = 1.15;
const STROKE_MIN = 0.022;

/** Coverage of a shape from its signed distance (negative inside). */
function inside(d: F, px: F): F {
  return smoothstep(px, px.negate(), d);
}

/** Coverage of an outline `abs(d)` wide. */
function outline(d: F, px: F): F {
  const half = px.mul(STROKE_PX).max(STROKE_MIN);
  return smoothstep(half.add(px), half, d.abs());
}

/** Signed distance to a convex polygon (exact inside, close enough outside for anti-aliasing). */
function polygon(p: V2, points: readonly Point[]): F {
  const cx = points.reduce((sum, [x]) => sum + x, 0) / points.length;
  const cy = points.reduce((sum, [, y]) => sum + y, 0) / points.length;
  let d: F | null = null;

  for (let i = 0; i < points.length; i++) {
    const [ax, ay] = points[i];
    const [bx, by] = points[(i + 1) % points.length];
    const len = Math.hypot(bx - ax, by - ay);
    let nx = (by - ay) / len;
    let ny = -(bx - ax) / len;

    // Outward: away from the centroid.
    if (nx * ((ax + bx) / 2 - cx) + ny * ((ay + by) / 2 - cy) < 0) {
      nx = -nx;
      ny = -ny;
    }

    const edge = p.x.sub(ax).mul(nx).add(p.y.sub(ay).mul(ny));
    d = d ? max(d, edge) : edge;
  }

  return d as F;
}

function disc(p: V2, x: number, y: number, r: number): F {
  return length(p.sub(vec2(x, y))).sub(r);
}

/** Two peaks, the larger one in front; the right face of each is shaded. */
export function mountains(p: V2, px: F): Mark & { shade: F } {
  const big: readonly Point[] = [[-0.06, -0.4], [-0.5, 0.3], [0.38, 0.3]];
  const small: readonly Point[] = [[0.3, -0.14], [0.02, 0.3], [0.56, 0.3]];
  const dBig = polygon(p, big);
  const dSmall = polygon(p, small);

  // The right half of each peak is the shaded face.
  const shadeBig = inside(dBig, px).mul(smoothstep(-0.01, 0.01, p.x.add(0.06)));
  const shadeSmall = inside(dSmall, px).mul(smoothstep(-0.01, 0.01, p.x.sub(0.3)));

  return {
    fill: max(inside(dBig, px), inside(dSmall, px)),
    shade: max(shadeBig, shadeSmall),
    stroke: max(outline(dBig, px), outline(dSmall, px).mul(float(1).sub(inside(dBig, px).mul(0.98)))),
  };
}

/** A mound: half a disc standing on a baseline; only the arc is inked. */
function mound(p: V2, x: number, y: number, r: number, px: F): Mark {
  const ring = length(p.sub(vec2(x, y))).sub(r);
  const above = smoothstep(px.negate(), px, float(y).sub(p.y));
  return { fill: inside(max(ring, p.y.sub(y)), px), stroke: outline(ring, px).mul(above) };
}

/** Two mounds side by side. */
export function hills(p: V2, px: F): Mark {
  const left = mound(p, -0.22, 0.2, 0.3, px);
  const right = mound(p, 0.2, 0.14, 0.36, px);
  return { fill: max(left.fill, right.fill), stroke: max(left.stroke, right.stroke) };
}

const TREES: readonly Point[] = [[-0.3, 0.02], [0.05, -0.2], [0.32, 0.08], [-0.02, 0.26]];
const TREE_R = 0.17;
const TRUNK = 0.09;

/** A stand of round crowns on short trunks. */
export function woods(p: V2, px: F): Mark {
  let fill: F = float(0);
  let stroke: F = float(0);

  for (const [x, y] of TREES) {
    const crown = disc(p, x, y, TREE_R);
    const trunk = polygon(p, [[x - 0.03, y + TREE_R - 0.02], [x + 0.03, y + TREE_R - 0.02], [x + 0.03, y + TREE_R + TRUNK], [x - 0.03, y + TREE_R + TRUNK]]);
    fill = max(fill, inside(crown, px));
    stroke = max(stroke, max(outline(crown, px), inside(trunk, px)));
  }

  return { fill, stroke };
}

const REEDS: readonly Point[] = [[-0.12, -0.16], [0.1, 0.0], [-0.1, 0.16]];
const REED_HALF = 0.24;

/** Short horizontal dashes, as marsh is drawn on maps. */
export function marsh(p: V2, px: F): Mark {
  let stroke: F = float(0);

  for (const [x, y] of REEDS) {
    const box = polygon(p, [[x - REED_HALF, y - 0.012], [x + REED_HALF, y - 0.012], [x + REED_HALF, y + 0.012], [x - REED_HALF, y + 0.012]]);
    stroke = max(stroke, outline(box, px).max(inside(box, px)));
  }

  return { fill: float(0), stroke };
}

/** A house (square body, pitched roof); `scale` grows it for a capital. */
export function city(p: V2, px: F, scale: F): Mark {
  const q = p.div(scale);
  const qpx = px.div(scale);
  const body = polygon(q, [[-0.22, -0.02], [0.22, -0.02], [0.22, 0.3], [-0.22, 0.3]]);
  const roof = polygon(q, [[-0.32, -0.02], [0, -0.34], [0.32, -0.02]]);
  const both = max(body, roof);

  return { fill: inside(both, qpx), stroke: max(outline(body, qpx), outline(roof, qpx)) };
}

/** A ring around a capital's house. */
export function capitalRing(p: V2, px: F, scale: F): F {
  return outline(length(p).sub(scale.mul(0.62)), px);
}
